import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Coffee } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useShopPause } from '@/hooks/use-shop-pause';

const SESSION_KEY = 'cycy-shop-pause-dismissed';

/**
 * Global notice the shop is temporarily paused.
 * Shown once per session; hidden inside admin.
 */
export default function ShopPauseDialog() {
  const { data } = useShopPause();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const isAdmin = pathname.startsWith('/admin');
  const paused = !!data?.paused;

  useEffect(() => {
    if (!paused || isAdmin) return;
    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(SESSION_KEY) === '1';
    } catch { /* ignore */ }
    if (!dismissed) setOpen(true);
  }, [paused, isAdmin]);

  if (!paused || isAdmin) return null;

  const message =
    (data?.message && data.message.trim()) ||
    'Unser Atelier & Online-Shop legen vorübergehend eine Pause auf unbestimmte Zeit ein. In diesem Zeitraum können leider keine Bestellungen aufgegeben oder versendet werden.';

  const handleDismiss = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch { /* ignore */ }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent className="max-w-[22rem] sm:max-w-[26rem] md:max-w-[30rem] overflow-hidden p-0 rounded-2xl border-border bg-card shadow-[var(--shadow-lifted)]">
        {/* Gold accent line */}
        <div
          className="h-[3px] w-full"
          style={{ background: 'var(--gradient-gold)' }}
          aria-hidden="true"
        />

        <div className="px-6 pt-6 pb-5 md:px-10 md:pt-8 md:pb-7 flex flex-col items-center text-center">
          {/* Icon */}
          <div className="relative mb-4">
            <div
              className="absolute inset-0 rounded-full blur-lg opacity-30 scale-125"
              style={{ background: 'hsl(var(--gold))' }}
              aria-hidden="true"
            />
            <div className="relative w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center bg-muted border border-border text-[hsl(var(--gold))]">
              <Coffee className="h-5 w-5 md:h-6 md:w-6" strokeWidth={1.75} aria-hidden="true" />
            </div>
          </div>

          <DialogHeader className="space-y-0">
            <DialogTitle className="font-heading text-2xl md:text-3xl leading-tight text-foreground">
              Vorübergehend geschlossen
            </DialogTitle>
            <DialogDescription className="sr-only">
              Hinweis zur aktuellen Shop-Pause
            </DialogDescription>
          </DialogHeader>

          <p className="mt-3 text-sm md:text-[0.95rem] leading-relaxed text-foreground/85 whitespace-pre-line">
            {message}
          </p>

          <div className="mt-4 w-8 h-px bg-border" aria-hidden="true" />

          <p className="mt-3 text-xs md:text-sm leading-relaxed text-muted-foreground">
            Du kannst dich weiterhin gerne in unserem Shop und unserer Galerie
            umsehen und inspirieren lassen. Sobald wir wieder öffnen, erfährst
            du es hier. Vielen Dank für dein Verständnis und deine Unterstützung!
          </p>

          <Button
            onClick={handleDismiss}
            size="sm"
            className="mt-5 h-10 px-8 rounded-full font-medium text-[hsl(var(--gold-foreground))] border-0 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-elegant)] hover:-translate-y-0.5 active:translate-y-0 transition-all"
            style={{ background: 'var(--gradient-gold)' }}
          >
            Verstanden
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
